import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, del } from '../lib/api.js';

interface PhishingCampaign {
  id: string;
  name: string;
  sender_name: string;
  sender_email: string;
  subject: string;
  target_users: Array<{ user_id: string; user_email: string; name?: string }>;
  status: string;
  click_rate?: number;
  report_rate?: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  result_summary?: {
    total_targets: number;
    opened: number;
    clicked: number;
    submitted_data: number;
    reported: number;
    click_rate: number;
    report_rate: number;
  };
}

interface CampaignsResponse {
  data: PhishingCampaign[];
  hasMore?: boolean;
}

export function PhishingCampaignsPage() {
  const { data: campaignsData, loading, error, refresh } = useApi<CampaignsResponse>('/sat/phishing');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formSenderName, setFormSenderName] = useState('');
  const [formSenderEmail, setFormSenderEmail] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const campaigns = campaignsData?.data ?? [];

  const completedCampaigns = campaigns.filter(c => c.status === 'completed' || c.status === 'active');
  const avgClickRate = completedCampaigns.length > 0
    ? Math.round(completedCampaigns.reduce((s, c) => s + (c.click_rate ?? c.result_summary?.click_rate ?? 0), 0) / completedCampaigns.length * 10) / 10
    : 0;
  const avgReportRate = completedCampaigns.length > 0
    ? Math.round(completedCampaigns.reduce((s, c) => s + (c.report_rate ?? c.result_summary?.report_rate ?? 0), 0) / completedCampaigns.length * 10) / 10
    : 0;
  const campaignsRun = campaigns.filter(c => c.status !== 'draft').length;

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await post('/sat/phishing', {
        name: formName.trim(),
        sender_name: formSenderName.trim(),
        sender_email: formSenderEmail.trim(),
        subject: formSubject.trim(),
        target_users: [],
      });
      setShowCreateForm(false);
      setFormName('');
      setFormSenderName('');
      setFormSenderEmail('');
      setFormSubject('');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLaunch(id: string) {
    setActionLoading(id);
    try {
      await post(`/sat/phishing/${id}?action=launch`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to launch campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleComplete(id: string) {
    setActionLoading(id);
    try {
      await post(`/sat/phishing/${id}?action=complete`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this campaign?')) return;
    setActionLoading(id);
    try {
      await del(`/sat/phishing/${id}`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete campaign');
    } finally {
      setActionLoading(null);
    }
  }

  function statusBadge(status: string) {
    const colors =
      status === 'active' || status === 'sending' ? 'bg-emerald-100 text-emerald-700' :
      status === 'completed' ? 'bg-blue-100 text-blue-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{status}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading phishing campaigns...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Phishing Simulations</h2>
      <p className="text-gray-600 mb-6">Run simulated phishing campaigns to test and improve user awareness.</p>

      <div className="flex items-center justify-between mb-4">
        <div className="grid grid-cols-3 gap-4 flex-1 mr-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Avg Click Rate</p>
            <p className={`text-2xl font-bold mt-1 ${avgClickRate > 15 ? 'text-red-600' : avgClickRate > 10 ? 'text-yellow-600' : 'text-emerald-600'}`}>
              {avgClickRate}%
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Avg Report Rate</p>
            <p className={`text-2xl font-bold mt-1 ${avgReportRate >= 50 ? 'text-emerald-600' : avgReportRate >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
              {avgReportRate}%
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Campaigns Run</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{campaignsRun}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 whitespace-nowrap"
        >
          + New Campaign
        </button>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="March Invoice Phish" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
              <input type="text" value={formSubject} onChange={(e) => setFormSubject(e.target.value)} placeholder="Your invoice is ready" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name</label>
              <input type="text" value={formSenderName} onChange={(e) => setFormSenderName(e.target.value)} placeholder="Accounts Payable" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email</label>
              <input type="email" value={formSenderEmail} onChange={(e) => setFormSenderEmail(e.target.value)} placeholder="noreply@example.com" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" required />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50">
              {submitting ? 'Creating...' : 'Create'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Campaign</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Sent</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Opened</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Clicked</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Reported</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Click Rate</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Report Rate</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No phishing campaigns yet. Create one to get started.</td></tr>
            ) : campaigns.map((c) => {
              const rs = c.result_summary;
              const sent = rs?.total_targets ?? c.target_users.length;
              const opened = rs?.opened ?? 0;
              const clicked = rs?.clicked ?? 0;
              const reported = rs?.reported ?? 0;
              const clickRate = c.click_rate ?? rs?.click_rate ?? 0;
              const reportRate = c.report_rate ?? rs?.report_rate ?? 0;
              return (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-3">{statusBadge(c.status)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{sent}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{opened}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{clicked}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{reported}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${clickRate > 15 ? 'text-red-600' : clickRate > 10 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                      {clickRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${reportRate >= 50 ? 'text-emerald-600' : reportRate >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {reportRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{c.started_at ? new Date(c.started_at).toLocaleDateString() : c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {c.status === 'draft' && (
                      <>
                        <button onClick={() => handleLaunch(c.id)} disabled={actionLoading === c.id} className="text-xs text-emerald-600 hover:text-emerald-800 mr-2 disabled:opacity-50">Launch</button>
                        <button onClick={() => handleDelete(c.id)} disabled={actionLoading === c.id} className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">Delete</button>
                      </>
                    )}
                    {c.status === 'active' && (
                      <button onClick={() => handleComplete(c.id)} disabled={actionLoading === c.id} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">Complete</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
